// „Разбивка по потребители" на хартията на отчета на заведението.
//
// Изнесена от `EstablishmentReportPreviewModal` при добавянето на бакшиша.
// Колоните „Бакшиш" и „За отчитане" се появяват САМО когато поне един
// сервитьор има бакшиш — иначе таблицата е точно досегашната (7 колони).
// Стойностите са от BE (`users_breakdown[].tip_total_amount` /
// `amount_to_report`); ред без тях (сервитьор без бакшиш, стар JSON) показва
// 0.00 бакшиш и „За отчитане" = в брой.

import { useTranslation } from "react-i18next";

import type { UserBreakdown } from "../../../../../api/reports";
import { hasUserTip } from "../../../../../lib/reportTips";
import { hasUserDeposit } from "../../../../../lib/reportDeposits";

type Props = {
  users: UserBreakdown[];
};

const formatCurrency = (amount: number | string | null | undefined) => {
  if (amount === null || amount === undefined) return "0.00";
  return Number(amount).toFixed(2);
};


export const EstablishmentUsersBreakdownTable = ({ users }: Props) => {
  const { t } = useTranslation();
  const showTips = users.some(hasUserTip);
  // Взетите депозити — колона „Депозити" преди „Обща сума"; „За отчитане" е
  // и при тях (в брой).
  const showDeposits = users.some(hasUserDeposit);
  const showAmountToReport = showTips || showDeposits;

  return (
    <div className="mb-6">
      <h3 className="text-base font-bold text-gray-800 bg-gray-200 px-2 py-1 mb-2">
        {t("establishmentReport.userBreakdown")}
      </h3>
      <table className="w-full border-collapse border border-gray-400 text-sm">
        <thead>
          <tr className="bg-gray-100">
            <th className="border border-gray-400 px-2 py-1 text-left">
              {t("establishmentReport.user")}
            </th>
            <th className="border border-gray-400 px-2 py-1 text-center">
              {t("establishmentReport.reports")}
            </th>
            <th className="border border-gray-400 px-2 py-1 text-center">
              {t("establishmentReport.bills")}
            </th>
            <th className="border border-gray-400 px-2 py-1 text-center">
              {t("establishmentReport.cashPayment")}
            </th>
            <th className="border border-gray-400 px-2 py-1 text-center">
              {t("establishmentReport.cardPayment")}
            </th>
            <th className="border border-gray-400 px-2 py-1 text-center">
              {t("establishmentReport.otherPayment")}
            </th>
            {showDeposits ? (
              <th className="border border-gray-400 px-2 py-1 text-right">
                {t("reportDeposits.section")}
              </th>
            ) : null}
            <th className="border border-gray-400 px-2 py-1 text-right">
              {t("establishmentReport.totalAmount")}
            </th>
            {showTips ? (
              <th className="border border-gray-400 px-2 py-1 text-right">
                {t("reportTips.section")}
              </th>
            ) : null}
            {showAmountToReport ? (
              <th className="border border-gray-400 px-2 py-1 text-right">
                {t("reportTips.amountToReport")}
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.user_id}>
              <td className="border border-gray-400 px-2 py-1">
                {user.user_name}
              </td>
              <td className="border border-gray-400 px-2 py-1 text-center">
                {user.reports_count}
              </td>
              <td className="border border-gray-400 px-2 py-1 text-center">
                {user.total_bills_count}
              </td>
              <td className="border border-gray-400 px-2 py-1 text-center">
                {formatCurrency(user.cash_amount)}
              </td>
              <td className="border border-gray-400 px-2 py-1 text-center">
                {formatCurrency(user.card_amount)}
              </td>
              <td className="border border-gray-400 px-2 py-1 text-center">
                {formatCurrency(user.other_amount)}
              </td>
              {showDeposits ? (
                <td className="border border-gray-400 px-2 py-1 text-right">
                  {formatCurrency(user.deposits_taken_amount)}
                </td>
              ) : null}
              <td className="border border-gray-400 px-2 py-1 text-right font-semibold">
                {formatCurrency(user.total_amount)}
              </td>
              {showTips ? (
                <td className="border border-gray-400 px-2 py-1 text-right">
                  {formatCurrency(user.tip_total_amount)}
                </td>
              ) : null}
              {showAmountToReport ? (
                <td className="border border-gray-400 px-2 py-1 text-right font-semibold">
                  {formatCurrency(user.amount_to_report ?? user.cash_amount)}
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
